// Layout business logic — framework-agnostic, zero React imports

import type { NavItem, KeyboardShortcut, ProductPin } from './types';
import { entityStore } from '@/stores/EntityStore';
import type { BreadcrumbItem } from './types';

/** Zone 2 ambient tool navigation items */
export const ZONE2_NAV_ITEMS: NavItem[] = [
  { id: 'inbox', labelKey: 'layout.sidebar.inbox', href: '/inbox', icon: 'inbox' },
  { id: 'activity', labelKey: 'layout.sidebar.activity', href: '/activity', icon: 'activity' },
  { id: 'my-work', labelKey: 'layout.sidebar.myWork', href: '/my', icon: 'mywork' },
  { id: 'chats', labelKey: 'layout.sidebar.chats', href: '/chats', icon: 'chat' },
  { id: 'tasks', labelKey: 'layout.sidebar.tasks', href: '/tasks', icon: 'task' },
  { id: 'docs', labelKey: 'layout.sidebar.docs', href: '/docs/workspace', icon: 'document' },
  { id: 'calendar', labelKey: 'layout.sidebar.calendar', href: '/calendar', icon: 'calendar' },
  { id: 'teams', labelKey: 'layout.sidebar.teams', href: '/teams', icon: 'team' },
  { id: 'projects', labelKey: 'layout.sidebar.projects', href: '/projects', icon: 'project' },
  { id: 'tags', labelKey: 'layout.sidebar.tags', href: '/tags', icon: 'tag' },
  { id: 'templates', labelKey: 'layout.sidebar.templates', href: '/templates', icon: 'template' },
  { id: 'pipelines', labelKey: 'layout.sidebar.pipelines', href: '/workflows', icon: 'workflow' },
];

/** Zone 2 navigation hrefs ordered by Cmd+1..7 */
export const ZONE2_SHORTCUT_HREFS = ZONE2_NAV_ITEMS.map((item) => item.href);

/** Keyboard shortcuts definitions */
export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  { id: 'search', key: 'k', meta: true, labelKey: 'layout.shortcuts.search', descriptionKey: 'layout.shortcuts.searchDesc' },
  { id: 'create', key: 'n', meta: true, labelKey: 'layout.shortcuts.create', descriptionKey: 'layout.shortcuts.createDesc' },
  { id: 'sidebar', key: 'b', meta: true, labelKey: 'layout.shortcuts.sidebar', descriptionKey: 'layout.shortcuts.sidebarDesc' },
  { id: 'rail', key: '.', meta: true, labelKey: 'layout.shortcuts.rail', descriptionKey: 'layout.shortcuts.railDesc' },
  { id: 'launcher', key: 'a', meta: true, shift: true, labelKey: 'layout.shortcuts.launcher', descriptionKey: 'layout.shortcuts.launcherDesc' },
  { id: 'goInbox', key: 'i', meta: true, shift: true, labelKey: 'layout.shortcuts.goInbox', descriptionKey: 'layout.shortcuts.goInboxDesc' },
  { id: 'markRelated', key: 'm,r', meta: false, labelKey: 'layout.shortcuts.markRelated', descriptionKey: 'layout.shortcuts.markRelatedDesc' },
  { id: 'markBlocked', key: 'm,b', meta: false, labelKey: 'layout.shortcuts.markBlocked', descriptionKey: 'layout.shortcuts.markBlockedDesc' },
];

/** Sidebar width constants */
export const SIDEBAR_WIDTH_COLLAPSED = 68;
export const SIDEBAR_WIDTH_EXPANDED = 240;

/** Rail width constraints */
export const RAIL_WIDTH_MIN = 280;
export const RAIL_WIDTH_MAX = 720;
export const RAIL_WIDTH_DEFAULT = 340;

/** Check if a path matches the current location */
export function isNavActive(currentPath: string, href: string): boolean {
  if (href === '/inbox') return currentPath === '/inbox' || currentPath === '/';
  if (href === '/my') return currentPath === '/my' || currentPath.startsWith('/my/');
  if (href === '/docs/workspace') return currentPath.startsWith('/docs') || currentPath.startsWith('/documents');
  return currentPath.startsWith(href);
}

/** Load sidebar expanded state from localStorage */
export function loadSidebarExpanded(): boolean {
  try {
    const stored = localStorage.getItem('sidebar_expanded');
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

/** Save sidebar expanded state to localStorage */
export function saveSidebarExpanded(expanded: boolean): void {
  try {
    localStorage.setItem('sidebar_expanded', String(expanded));
  } catch {
    // localStorage unavailable
  }
}

/** Load product pins from localStorage */
export function loadProductPins(): ProductPin[] {
  try {
    const stored = localStorage.getItem('sidebar_pins');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/** Save product pins to localStorage */
export function saveProductPins(pins: ProductPin[]): void {
  try {
    localStorage.setItem('sidebar_pins', JSON.stringify(pins));
  } catch {
    // localStorage unavailable
  }
}

/** Load rail width from localStorage */
export function loadRailWidth(): number {
  try {
    const stored = localStorage.getItem('rail_width');
    if (stored) {
      const width = parseInt(stored, 10);
      if (width >= RAIL_WIDTH_MIN && width <= RAIL_WIDTH_MAX) return width;
    }
  } catch {
    // localStorage unavailable
  }
  return RAIL_WIDTH_DEFAULT;
}

/** Save rail width to localStorage */
export function saveRailWidth(width: number): void {
  try {
    localStorage.setItem('rail_width', String(width));
  } catch {
    // localStorage unavailable
  }
}

/** Check if a string looks like a UUID or opaque ID (not a human-readable slug) */
export function isIdLikeSegment(seg: string): boolean {
  // UUIDs, ULIDs, nanoids, or any hex-heavy string longer than 8 chars
  return /^[0-9a-f-]{8,}$/i.test(seg) || /^[0-9A-Za-z_-]{20,}$/.test(seg);
}

/** Build breadcrumb items from current pathname */
export function buildBreadcrumbs(pathname: string, t: (key: string) => string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [{ label: t('layout.sidebar.inbox'), href: '/inbox' }];

  const crumbs: BreadcrumbItem[] = [];
  let path = '';
  for (const seg of segments) {
    path += `/${seg}`;
    const key = `layout.breadcrumb.${seg}`;
    let label: string;
    if (t(key) !== key) {
      label = t(key);
    } else if (isIdLikeSegment(seg)) {
      // Use entity title from store if available, otherwise show truncated ID
      const entity = entityStore.currentEntity;
      label = (entity && entity.id === seg && entity.title) ? entity.title : seg.substring(0, 8) + '...';
    } else {
      label = seg.charAt(0).toUpperCase() + seg.slice(1);
    }
    crumbs.push({ label, href: path });
  }
  return crumbs;
}
